/* eslint-disable @typescript-eslint/no-use-before-define */
import {
  Component, ExistingProvider, forwardRef, OnInit, OnDestroy, HostListener,
} from '@angular/core';
import {
  UntypedFormGroup, UntypedFormBuilder, Validators, NG_VALIDATORS, NG_VALUE_ACCESSOR, ControlValueAccessor, Validator, ValidationErrors,
} from '@angular/forms';
import { IUser } from 'typings/doenkids/doenkids';
import { Observable, Subject, combineLatest } from 'rxjs';
import { noop, isNil } from 'lodash';
import { takeUntil, map } from 'rxjs/operators';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { PermissionProvider } from 'src/providers/permission.provider';
import { DoenkidsSessionProvider } from 'src/providers/session.provider';
import { UserAiEnabledService } from 'src/api/customer/user-ai-enabled/user-ai-enabled.service';
import { I18nToastProvider } from 'src/providers/i18n-toast.provider';

export const USER_FORM_CONTROL_VALUE_ACCESSOR: ExistingProvider = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => UserFormComponent),
  multi: true,
};

export const USER_FORM_CONTROL_VALIDATOR: ExistingProvider = {
  provide: NG_VALIDATORS,
  useExisting: forwardRef(() => UserFormComponent),
  multi: true,
};

@Component({
    selector: 'app-user-form',
    templateUrl: './user-form.component.html',
    styleUrls: ['./user-form.component.scss'],
    providers: [USER_FORM_CONTROL_VALUE_ACCESSOR, USER_FORM_CONTROL_VALIDATOR],
    standalone: false
})
export class UserFormComponent implements OnInit, ControlValueAccessor, Validator, OnDestroy {
  private stop$ = new Subject<void>();

  public form: UntypedFormGroup;

  public editingAnExistingUser$: Observable<boolean>;

  public title: string;

  public isAdmin$: Observable<boolean>;

  public isManagement$: Observable<boolean>;

  /**
   * The per-user AI flag.
   *
   * Deliberately NOT a control on `form`: `writeValue` copies every property it
   * finds onto the form and `ngOnInit` emits the whole form value upwards, so a
   * control here would ride along in the ordinary user save. The API excludes
   * `ai_enabled` from that endpoint's whitelist precisely so it cannot be set
   * that way, and it has its own DoenKids-only route instead.
   */
  public aiEnabled = false;

  public savingAiEnabled = false;

  /** What the flag is in the database, so a save knows whether to write it */
  private storedAiEnabled = false;

  /** Value we will call whenever our form is touched */
  private onTouchedCallback: Function = noop;

  /** Function we will call whenever the value changes */
  private onChangeCallback: (val) => void = noop;

  @HostListener('blur') on() {
    this.onTouchedCallback();
  }

  constructor(
    fb: UntypedFormBuilder,
    private $permission: PermissionProvider,
    private $session: DoenkidsSessionProvider,
    private userAiEnabledService: UserAiEnabledService,
    private $i18nToastProvider: I18nToastProvider,
  ) {
    this.form = fb.group({
      email: ['', [Validators.required, Validators.email]],
      active: [false],
      user_role: ['user', Validators.required],
      media_uuid: [''],
      id: [],
    });

    this.editingAnExistingUser$ = this.form.valueChanges.pipe(
      map((form) => !isNil(form.id)),
    );

    this.isAdmin$ = this.$session.isAdmin$;

    this.isManagement$ = combineLatest([
      this.isAdmin$,
      this.$permission.hasWritePermissionOnAtLeastOneCustomerOUInCurrentNodeTree$,
    ]).pipe(
      map((roles) => roles.includes(true)),
    );
  }

  ngOnInit() {
    this.form.valueChanges.pipe(takeUntil(this.stop$)).subscribe((formValue) => {
      this.onChangeCallback(formValue);
    });
  }

  ngOnDestroy(): void {
    this.stop$.next();
  }

  writeValue(value: IUser) {
    if (value) {
      for (const propertyKey in value) {
        const formEntry = this.form.get(propertyKey);
        formEntry?.setValue(value[propertyKey]);
      }

      this.aiEnabled = value.ai_enabled === true;
      this.storedAiEnabled = this.aiEnabled;
    }
  }


  /** True once there is a user to set the flag on. */
  public get canEditAiEnabled(): boolean {
    return !isNil(this.form.get('id').value);
  }

  /**
   * Records the flag, to be written when the form is saved.
   *
   * Not written on change: the save button sits on the same row as this
   * checkbox, so saving behind it reads as a bug even though the flag has its
   * own endpoint. Marking the form dirty is what enables that button.
   */
  public toggleAiEnabled(checked: boolean) {
    this.aiEnabled = checked;
    this.onChangeCallback(this.form.value);
  }

  /**
   * Writes the flag, if it changed, through its own route.
   *
   * Called by the page's save. It cannot ride along with the user update:
   * `ai_enabled` is deliberately absent from that endpoint's property
   * whitelist, so this is the only way to set it.
   */
  public async persistAiEnabled(): Promise<void> {
    const userId = this.form.get('id').value;

    if (isNil(userId) || this.aiEnabled === this.storedAiEnabled) {
      return;
    }

    this.savingAiEnabled = true;

    try {
      const updatedUser = await this.userAiEnabledService.update(userId, this.aiEnabled);

      // Reflected from the response rather than from what was ticked, so the
      // checkbox can never disagree with what was stored.
      //
      this.aiEnabled = updatedUser?.ai_enabled === true;
      this.storedAiEnabled = this.aiEnabled;
    } catch (error) {
      this.aiEnabled = this.storedAiEnabled;
      this.$i18nToastProvider.error(_('user.ai_enabled.save_failed'));
    }

    this.savingAiEnabled = false;
  }

  registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }

  validate(): ValidationErrors {
    return this.form.errors;
  }
}
